import React, { useContext, useEffect, useState } from 'react'
import { classes, style } from 'typestyle'
import { Link, useParams } from 'react-router-dom'

/* Context ====================================================================================== */
import { NavigationContext } from '../../__aeki__/contexts/navigation'

/* Core ========================================================================================= */
import { addSnippet } from '../../__aeki__/core/snippet-manager'
import useArray from '../../__aeki__/hooks/use-array'

/* Hooks ======================================================================================== */
import useFetch from '../../__aeki__/hooks/use-fetch'
import useValue from '../../__aeki__/hooks/use-value'

/* Components =================================================================================== */
import { Box } from '../../__aeki__/components/box/box.component'
import { Button } from '../../__aeki__/components/button'
import { Text } from '../../__aeki__/components/text/text.component'
import { Snippet, SnippetHead, SnippetBody } from '../../__aeki__/modules/layouts/snippet'
import { Line } from '../../__aeki__/components/line'

/* Styles ======================================================================================= */
import { p12 } from '../../__aeki__/styles/styleset/padding/p12'
import { paddingLeftInner } from '../../__aeki__/styles/default/padding'
import { pt12 } from '../../__aeki__/styles/styleset/padding/pt12'
import { pb12 } from '../../__aeki__/styles/styleset/padding/pb12'
import { pb8 } from '../../__aeki__/styles/styleset/padding/pb8'
import { pt8 } from '../../__aeki__/styles/styleset/padding/pt8'

export default addSnippet('ClassesChildren', (props: any) => {
  const { title, row } = props
  const { navigation, layout, schema, ...rest } = useContext(NavigationContext)
  const { groupId } = useParams()

  const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/classes-children/${groupId}`
  const [data, $data] = useFetch({ data: [], count: 0 }, { url: apiUrl })
  const [patch, $patch] = useFetch({ data: [], count: 0 }, { url: apiUrl })
  const [toggled, $toggled] = useValue(null)
  const [students, $students] = useArray<any>([])

  useEffect(() => {
    if (data.mounted && Object.keys(row).length) {
      $data
        .get({
          url: `${apiUrl}/${row.body.program.id}`,
        })
        .then((payload: any) => {
          $students.set(row.body.students || [])
        })
    }
  }, [data.mounted, row])

  useEffect(() => {
    if (toggled === false) {
      $patch
        .post({
          url: `${apiUrl}/${row.body.program.id}/${row.id}`,
          body: students.map((student: any) => {
            return {
              id: student.id,
            }
          }),
        })
        .then(() => {
          props.sync()
        })
    }
  }, [toggled])

  return (
    <Snippet>
      <SnippetHead title={'Students'}>
        <Button
          label={toggled ? 'Save' : students?.length ? 'Edit' : 'Add'}
          baseline="primary"
          size="sm"
          square
          onClick={() => $toggled.set((prev: any) => !prev)}
        />
      </SnippetHead>
      <Line />
      {!toggled ? (
        <>
          {!students?.length ? (
            <SnippetBody>
              <Box baseline="none" className={pt12}>
                <Text>No student assigned to this class.</Text>
              </Box>
            </SnippetBody>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Student Name
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Primary Guardian
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Primary Guardian Contact
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Secondary Guardian
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Secondary Guardian Contact
                  </Text>
                </td>
              </thead>
              <tbody>
                {data.payload?.data
                  ?.filter((a: any) => students.find((b: any) => b.id === a.id))
                  ?.map((client: any, key: any) => {
                    const {
                      child,
                      primaryGuardian,
                      secondaryGuardian,
                    } = client.body.generalInformation

                    return (
                      <tr key={key}>
                        <td
                          className={classes(
                            paddingLeftInner,
                            pt12,
                            pb12,
                            style({ verticalAlign: 'middle' }),
                          )}
                        >
                          <Box>
                            <Link
                              to={`/a/groups/${row.group}/base/childcare-clients/table/${client.id}`}
                            >
                              <Text link>{child.fullName || '-'}</Text>
                            </Link>
                          </Box>
                          <Box>
                            <Text size="xs" semiBold shrink>
                              DOB:{' '}
                            </Text>
                            <Text size="xs" shrink>
                              {child.dateOfBirth || '-'}
                            </Text>
                          </Box>
                        </td>
                        <td
                          className={classes(
                            paddingLeftInner,
                            pt12,
                            pb12,
                            style({ verticalAlign: 'middle' }),
                          )}
                        >
                          <Box>
                            <Text>
                              {primaryGuardian.fullName && primaryGuardian.fullName !== ' '
                                ? primaryGuardian.fullName
                                : '-'}
                            </Text>
                          </Box>
                        </td>
                        <td
                          className={classes(
                            paddingLeftInner,
                            pt12,
                            pb12,
                            style({ verticalAlign: 'middle' }),
                          )}
                        >
                          <Box>
                            <Text>{primaryGuardian.phone || '-'}</Text>
                          </Box>
                        </td>
                        <td
                          className={classes(
                            paddingLeftInner,
                            pt12,
                            pb12,
                            style({ verticalAlign: 'middle' }),
                          )}
                        >
                          <Box>
                            <Text>
                              {secondaryGuardian.fullName && secondaryGuardian.fullName !== ' '
                                ? secondaryGuardian.fullName
                                : '-'}
                            </Text>
                          </Box>
                        </td>
                        <td
                          className={classes(
                            paddingLeftInner,
                            pt12,
                            pb12,
                            style({ verticalAlign: 'middle' }),
                          )}
                        >
                          <Box>
                            <Text>{secondaryGuardian.phone || '-'}</Text>
                          </Box>
                        </td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <>
          <Box flex fullWidth>
            <table
              style={{
                width: '100%',
              }}
            >
              <thead>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Student Name
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Primary Guardian
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Primary Guardian Contact
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Secondary Guardian
                  </Text>
                </td>
                <td
                  className={classes(
                    paddingLeftInner,
                    pt8,
                    pb8,
                    style({ verticalAlign: 'middle' }),
                  )}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                >
                  <Text size="sm" shrink baseline="surface" lighter>
                    Secondary Guardian Contact
                  </Text>
                </td>
                <td
                  className={classes(style({ verticalAlign: 'middle' }))}
                  style={{
                    borderBottom: '1px solid rgb(240,240,240)',
                  }}
                ></td>
              </thead>
              <tbody>
                {data.payload.data.map((client: any, key: any) => {
                  const {
                    child,
                    primaryGuardian,
                    secondaryGuardian,
                  } = client.body.generalInformation
                  const exists = students.findIndex((a: any) => a.id === client.id)

                  return (
                    <tr key={key}>
                      <td
                        className={classes(
                          paddingLeftInner,
                          pt12,
                          pb12,
                          style({ verticalAlign: 'middle' }),
                        )}
                      >
                        <Box>
                          <Link
                            to={`/a/groups/${row.group}/base/childcare-clients/table/${client.id}`}
                          >
                            <Text link>{child.fullName}</Text>
                          </Link>
                        </Box>
                        <Box>
                          <Text size="xs" semiBold shrink>
                            DOB:{' '}
                          </Text>
                          <Text size="xs" shrink>
                            {child.dateOfBirth || '-'}
                          </Text>
                        </Box>
                      </td>
                      <td
                        className={classes(
                          paddingLeftInner,
                          pt12,
                          pb12,
                          style({ verticalAlign: 'middle' }),
                        )}
                      >
                        <Box>
                          <Text>
                            {primaryGuardian.fullName && primaryGuardian.fullName !== ' '
                              ? primaryGuardian.fullName
                              : '-'}
                          </Text>
                        </Box>
                      </td>
                      <td
                        className={classes(
                          paddingLeftInner,
                          pt12,
                          pb12,
                          style({ verticalAlign: 'middle' }),
                        )}
                      >
                        <Box>
                          <Text>{primaryGuardian.phone || '-'}</Text>
                        </Box>
                      </td>
                      <td
                        className={classes(
                          paddingLeftInner,
                          pt12,
                          pb12,
                          style({ verticalAlign: 'middle' }),
                        )}
                      >
                        <Box>
                          <Text>
                            {secondaryGuardian.fullName && secondaryGuardian.fullName !== ' '
                              ? secondaryGuardian.fullName
                              : '-'}
                          </Text>
                        </Box>
                      </td>
                      <td
                        className={classes(
                          paddingLeftInner,
                          pt12,
                          pb12,
                          style({ verticalAlign: 'middle' }),
                        )}
                      >
                        <Box>
                          <Text>{secondaryGuardian.phone || '-'}</Text>
                        </Box>
                      </td>
                      <td className={classes(p12, style({ verticalAlign: 'middle' }))}>
                        <Box flex alignRight>
                          <Button
                            label={exists > -1 ? 'Remove' : 'Add'}
                            baseline={exists > -1 ? 'secondary' : 'background'}
                            size="sm"
                            square
                            onClick={() => {
                              if (exists > -1) {
                                $students.pop(exists)
                              } else {
                                $students.push(client)
                              }
                            }}
                          />
                        </Box>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Box>
        </>
      )}
    </Snippet>
  )
})
